import React, { useEffect, useRef, useState } from "react";
import { FaUser, FaPhone, FaEnvelope, FaLock, FaMapMarkerAlt, FaUserCog, FaSpinner } from "react-icons/fa";
import Dropdown from "../Common/Dropdown/main";
import { User } from "../../hook/types/users";

interface UserInfo {
  userName: string;
  active: boolean;
  userID: string;
  contactNumber: string;
  userEmail?: string | null;
  role: string;
  address: string;
  createdBy: string;
  userPassword: string;
}

interface UserFormProps {
  initialData: User;
  onSubmit: (formData: User) => void;
  onCancel?: () => void;
  isOpen: boolean;
  isLoading?:boolean
  disabled?:boolean
  id?: number;
}

const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  disabled,
  isOpen,
  isLoading,
  id
}) => {
  const [formData, setFormData] = useState<User>({
    ...initialData,
    // createdBy: initialData.createdBy || "admin",
    userActiveInactive: initialData.userActiveInactive ?? true,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [activeStatus, setActiveStatus] = useState<boolean>(initialData.userActiveInactive ?? true);

  //const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  // useEffect(() => {
  //   setFormData(initialData);
  // }, [initialData]);
   useEffect(() => {
        setFormData({
            ...initialData,
            userActiveInactive: initialData.userActiveInactive ?? true,
        });
        setActiveStatus(initialData.userActiveInactive ?? true);
    }, [initialData]);

  const useOutsideClick = (callback: () => void) => {
    const ref = useRef<HTMLDivElement>(null);
  
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          callback();
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [callback]);
  
    return ref;
  };
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const roleDropdownOpenRef = useOutsideClick(() => setRoleDropdownOpen(false));
  

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    // Check if the field is for phone number and validate it
    if (id === "userContact") {
      // Allow only numbers and limit to 10 digits
      const phoneNumber = value.replace(/\D/g, ""); // Remove non-digit characters
      if (phoneNumber.length > 10) return; // Prevent further input after 10 digits
  
      setFormData({
        ...formData,
        [id as any]: phoneNumber,
      });
    } else {
      setFormData({
        ...formData,
        [id]: value,
      });
    }
  };
  
const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newStatus = e.target.checked;
        setActiveStatus(newStatus);
        setFormData((prev) => ({ ...prev, userActiveInactive: newStatus }));
    };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const fields = ["userName", "userContact", "userPassword"];

    fields.forEach(field => {
      if (!formData[field as keyof User]) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1')} is required.`;
      }
    });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.userEmail && !emailRegex.test(formData.userEmail)) {
      newErrors.userEmail = "Email ID is not valid.";
    }
     // Password length validation
  if (formData.userPassword && formData.userPassword.length < 6) {
    newErrors.userPassword = "Password must be at least 6 characters long.";
  }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  if (!isOpen) {
    return null;
  }

  // --- Toggle Switch Component ---
    const ToggleSwitch: React.FC = () => (
        <div className="flex items-center space-x-2">
            <label htmlFor="active-toggle" className="block text-sm text-gray-700 font-bold flex items-center">
                <FaUserCog className="mr-2 h-5 text-green-500 w-5" /> Status:
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
                <input
                    type="checkbox"
                    id="active-toggle"
                    className="sr-only peer"
                    checked={activeStatus}
                    onChange={handleToggleChange}
                    disabled={isLoading}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {activeStatus ? 'Active' : 'Inactive'}
                </span>
            </label>
        </div>
    );
    // --- End Toggle Switch Component ---

  return (
    <div className="bg-white rounded-lg w-full max-w-3xl mx-auto p-4">
      <h1 className="text-lg font-bold mb-4 flex justify-between items-center">
        User Information <span className="text-md italic pr-10">{id ? id : ""}</span>
      </h1>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
          <div className="flex gap-1 flex-col ">
            <label htmlFor="userName" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaUser className="mr-2 h-5 text-blue-500 w-5" />
              User Name
            </label>
            <input
              type="text"
              id="userName"
              value={formData?.userName || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full text-gray-700 leading-tight focus:outline-none p-2 focus:shadow-outline"
            />
            {errors?.userName && <p className="text-red-500 text-xs italic">{errors?.userName}</p>}
          </div>
         
          <div className="flex gap-1 flex-col">
            <label htmlFor="userContact" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaPhone className="mr-2 h-5 text-yellow-500 w-5" />
              Contact Number
            </label>
            <input
              type="number"
              id="userContact"
              value={formData?.userContact || ""}
              onChange={
                handleChange}
              className="shadow appearance-none border rounded w-full text-gray-700 leading-tight focus:outline-none p-2 focus:shadow-outline"
            />
            {errors?.userContact && <p className="text-red-500 text-xs italic">{errors?.userContact}</p>}
          </div>
          <div className="flex gap-1 flex-col">
            <label htmlFor="userEmail" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaEnvelope className="mr-2 h-5 text-red-500 w-5" />
              Email ID
            </label>
            <input
              type="email"
              id="userEmail"
              value={formData?.userEmail || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full text-gray-700 leading-tight focus:outline-none p-2 focus:shadow-outline"
            />
            {errors?.userEmail && <p className="text-red-500 text-xs italic">{errors?.userEmail}</p>}
          </div>
          <div className="flex gap-1 flex-col" >
            <label htmlFor="role" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaUserCog className="mr-2 h-5 text-purple-500 w-5" />
              Role
            </label>
            {/* <Dropdown
            disabled={disabled}
              options={["Admin", "Payment Collector",]}
              onSelect={(e: any) =>{ 
                setFormData({ ...formData, roles: [{roleName:e=="Admin"?'ADMIN':'PAYMENT_COLLECTOR'}] })
              }}
              activeOption={
                formData?.roles[0].roleName
                
              }
              className="w-[100%] "
              className2="py-2"

            /> */}
  <div
  ref={roleDropdownOpenRef}
  onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}>
              <Dropdown
                disabled={disabled}
                // options={["Admin", "Payment Collector"]}
                options={["Payment Collector"]}
                onSelect={(e: any) => {
                  setFormData({
                    ...formData,
                    roles: [{ roleName: e === "Admin" ? "ADMIN" : "PAYMENT_COLLECTOR" }],
                  });
                  setRoleDropdownOpen(false); // Close dropdown after selection
                }}
                activeOption={formData?.roles[0]?.roleName}
                className="w-[100%]"
                className2="py-2"
                isOpen={roleDropdownOpen}
                setIsOpen={setRoleDropdownOpen}
                closeOtherDropdown={() => {}}
              />
            </div>

            {errors?.role && <p className="text-red-500 text-xs italic">{errors?.role}</p>}
          </div>
          <div className="flex gap-1 flex-col">
            <label htmlFor="createdBy" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaUser className="mr-2 h-5 text-blue-500 w-5" />
              Created By
            </label>
            <input
              type="text"
              id="createdBy"
              disabled
              value={ "Admin"}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.createdBy && <p className="text-red-500 text-xs italic">{errors?.createdBy}</p>}
          </div>
          {/* <div className="flex gap-1 flex-col">
            <label htmlFor="address" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaMapMarkerAlt className="mr-2 h-5 text-teal-500 w-5" />
              Address
            </label>
            <textarea
              id="address"
              value={
                formData?.address 
                ||
                 ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.address && <p className="text-red-500 text-xs italic">{errors?.address}</p>}
          </div> */}
         
          <div className="flex gap-1 flex-col">
            <label htmlFor="userPassword" className="block text-sm text-gray-700 font-bold flex items-center">
              <FaLock className="mr-2 h-5 text-gray-500 w-5" />
              Password
            </label>
            <input
              type="text"
              id="userPassword"
              value={formData?.userPassword || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.userPassword && <p className="text-red-500 text-xs italic">{errors?.userPassword}</p>}
          </div>
           {/* Active/Inactive Toggle Button */}
                    <div className="flex gap-1 flex-col justify-center"> {/* Added justify-center for vertical alignment */}
                        <ToggleSwitch />
                    </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end mt-4 space-y-2 sm:space-y-0 sm:space-x-4">
          <button
          disabled={isLoading}
            type="submit"
            className="bg-primary hover:bg-secondary text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
{isLoading?<p className="flex justify-center items-center"><FaSpinner className="animate-spin mr-2"/> Updating...</p>:"Save"}
  
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-red-400 hover:bg-red-500 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
